import { useState, type FormEvent } from 'react';
import { ArrowRight, Mail, Lock } from 'lucide-react';
import { useSession } from '../contexts/SessionContext';
import { ApiError } from '../api/client';
import { Button } from '../components/ui/Button';
import { Input, Label } from '../components/ui/Input';
import { cn } from '../lib/cn';

export function LoginScreen() {
  const { login } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen relative bg-zinc-50 flex items-center justify-center px-4 py-12 overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 [background:radial-gradient(60%_60%_at_50%_0%,rgba(26,58,107,0.08),transparent_70%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.18] [background-image:linear-gradient(rgb(0_0_0/0.06)_1px,transparent_1px),linear-gradient(90deg,rgb(0_0_0/0.06)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]"
      />

      <div className="w-full max-w-[400px] animate-fade-in-up">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-zinc-900 to-zinc-700 flex items-center justify-center shadow-card">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9l9-6 9 6-9 6-9-6z" />
              <path d="M3 15l9 6 9-6" />
            </svg>
          </div>
          <span className="text-lg font-semibold tracking-tight2 text-zinc-900">Sitekit</span>
        </div>

        <div className="bg-white border border-zinc-200/80 rounded-2xl shadow-soft p-7 sm:p-8">
          <div className="text-center mb-6">
            <h1 className="text-xl font-semibold tracking-tight2 text-zinc-900">Welcome back</h1>
            <p className="text-[13px] text-zinc-500 mt-1">Sign in to your dashboard</p>
          </div>

          <form onSubmit={onSubmit} aria-label="Login" className="flex flex-col gap-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
                <Input
                  id="email"
                  type="email"
                  className="pl-9"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                  disabled={submitting}
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
                <Input
                  id="password"
                  type="password"
                  className="pl-9"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  disabled={submitting}
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div className={cn(
                'px-3 py-2.5 bg-red-50 border border-red-200 text-red-800 text-[12.5px] rounded-lg',
                'animate-fade-in',
              )}>
                {error}
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              loading={submitting}
              disabled={submitting || !email || !password}
              rightIcon={<ArrowRight className="h-3.5 w-3.5" />}
              className="mt-1"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>

        <p className="text-[11.5px] text-zinc-400 mt-6 text-center leading-relaxed">
          Account created by your administrator.<br />
          Contact them to reset your password.
        </p>
      </div>
    </div>
  );
}
